import React, { useState, useMemo, useEffect } from "react";
import { Box, Typography, IconButton } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { useStore } from "@stores/root-store";
import { useInviteModal } from "@components/ModalContext/InviteModalContext";
import UserDetails from "@components/layout/superadmin-layout/pages/userDetails/UserDetails";
import { useUserContext } from "@components/layout/superadmin-layout/SuperAdminLayout";
import InviteModal from "@components/layout/superadmin-layout/components/inviteModal/InviteModal";
import DeleteUserModal from "@components/layout/superadmin-layout/components/deleteUserModal/DeleteUserModal";
import DataGrid from "@components/shared/DataGrid/DataGrid";
import { AddTokensModal } from "@components/layout/superadmin-layout/addTokensModal/AddTokensModal";
import {
  GridColDef,
  GridRenderCellParams,
  GridPaginationModel,
} from "@mui/x-data-grid";
import DeleteIcon from "@assets/componentIcons/DeleteIcon";
import addIcon from "@assets/icons/AddCardSharp.svg";
import { constRoute } from "@utils/route";

const Users = () => {
  const { isModalOpen } = useInviteModal();
  const [userEmailToDelete, setUserEmailToDelete] = useState<string | null>(
    null,
  );
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openAddCreditsModal, setOpenAddCreditsModal] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [selectedUserForCredits, setSelectedUserForCredits] = useState<{
    name: string;
    email: string;
  } | null>(null);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 20,
  });

  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const email = searchParams.get("email");

  const {
    user: { getAllUsers: allUsers, onDeleteUser, setAllUsers, receiveAllUsers },
  } = useStore(null);

  const { userDetails, updateUserDetails } = useUserContext();

  useEffect(() => {
    (async () => {
      setLoadingUsers(true);
      await receiveAllUsers();
      setLoadingUsers(false);
    })();
  }, []);

  const handleUserDelete = async (email: string) => {
    const updatedUsers =
      allUsers?.["account holders"]?.filter((el) => el.user_email !== email) ||
      [];

    onDeleteUser(email);
    setAllUsers({ ...allUsers, "account holders": updatedUsers });
  };

  // Reset all modals
  const resetAllModals = () => {
    setOpenDeleteModal(false);
    setOpenAddCreditsModal(false);
    setUserEmailToDelete(null);
    setSelectedUserForCredits(null);
  };

  // Handle delete action
  const handleDelete = (e: React.MouseEvent, params: GridRenderCellParams) => {
    e.preventDefault();
    e.stopPropagation();

    // Reset all modals first
    resetAllModals();

    // Set delete specific states
    const userEmail = params.row.user_email;
    const accountHolder = params.row.account_holder;

    setUserEmailToDelete(userEmail);
    setOpenDeleteModal(true);
  };

  const handleAddCredits = (
    e: React.MouseEvent,
    params: GridRenderCellParams,
  ) => {
    e.preventDefault();
    e.stopPropagation();

    resetAllModals();
    setSelectedUserForCredits({
      name: params.row.account_holder,
      email: params.row.user_email,
    });
    setOpenAddCreditsModal(true);
  };

  // Handle pagination changes
  const handlePaginationModelChange = (newModel: GridPaginationModel) => {
    setPaginationModel(newModel);
  };

  const columns: GridColDef[] = [
    {
      field: "account_holder",
      headerName: "Name",
      flex: 2,
      renderCell: (params: GridRenderCellParams) => (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            cursor: "pointer",
            py: 0.5,
            width: "100%",
          }}
          onClick={() =>
            navigate(
              `${constRoute.masteradminUsers}?email=${encodeURIComponent(
                params.row.user_email,
              )}`,
            )
          }
        >
          <Typography
            variant="body2"
            fontWeight="bold"
            sx={{
              color: "#000",
              borderBottom: "1px solid #000",
              display: "inline-block",
              lineHeight: 1.2,
              padding: 0,
              margin: 0,
              width: "fit-content",
            }}
          >
            {params.row.account_holder}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", fontSize: "0.8rem" }}
          >
            {params.row.user_email}
          </Typography>
        </Box>
      ),
    },
    {
      field: "plan",
      headerName: "Plan",
      flex: 1,
    },
    {
      field: "entity_type",
      headerName: "Entity Type",
      flex: 1,
    },
    {
      field: "chat_credits",
      headerName: "Search Credits",
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        const creditsField =
          params.row["Chat Credits used / Chat Credits purchased"];

        if (creditsField) {
          return creditsField;
        }

        return "N/A";
      },
    },
    {
      field: "assistant_credits",
      headerName: "Assistant Credits",
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        const creditsField =
          params.row["Assistant Credits used / Assistant Credits purchased"];

        if (creditsField) {
          return creditsField;
        }

        return "N/A";
      },
    },
    {
      field: "last_logged_in",
      headerName: "Last Login",
      flex: 1,
    },
    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        return (
          <Box sx={{ display: "flex", gap: 1 }}>
            {/* Add Credits Icon */}
            <IconButton
              size="small"
              onClick={(e) => {
                handleAddCredits(e, params);
              }}
              sx={{
                padding: "4px",
                minWidth: "auto",
                "&:hover": {
                  backgroundColor: "rgba(0, 184, 64, 0.08)",
                },
              }}
              title="Add credits"
              data-testid="add-credits-button"
            >
              <img
                src={addIcon}
                alt="add credits"
                style={{
                  width: "16px",
                  height: "16px",
                  display: "block",
                  pointerEvents: "none",
                }}
              />
            </IconButton>

            {/* Delete Icon */}
            <IconButton
              size="small"
              onClick={(e) => {
                handleDelete(e, params);
              }}
              sx={{
                padding: "4px",
                minWidth: "auto",
                "&:hover": {
                  backgroundColor: "rgba(244, 67, 54, 0.04)",
                },
              }}
              title="Delete user"
              data-testid="delete-button"
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        );
      },
    },
  ];

  const rows = useMemo(() => {
    const seenEmails = new Set<string>();
    return (
      allUsers?.["account holders"]
        ?.filter((el) => {
          if (!el?.user_email) return true;
          if (seenEmails.has(el.user_email)) return false;
          seenEmails.add(el.user_email);
          return true;
        })
        .map((el, index) => ({
          id: el.user_email || index,
          ...el,
        })) || []
    );
  }, [allUsers]);

  if (email && userDetails) {
    return (
      <>
        <UserDetails
          userDetails={userDetails}
          updateUserDetails={updateUserDetails}
        />
        {isModalOpen && <InviteModal />}
      </>
    );
  }

  return (
    <Box sx={{ p: 2.5 }}>
      <Typography
        variant="h5"
        sx={{
          borderBottom: "1px solid #E0E0E0",
          paddingBottom: 2,
          margin: "32px 0 24px 0", // Increased top margin to 32px for better spacing with new header
          fontWeight: 600,
          fontSize: "20px",
          lineHeight: "28px",
        }}
      >
        Manage Users
      </Typography>

      <Box sx={{ height: "calc(100vh - 300px)", width: "100%", mt: 2 }}>
        <DataGrid
          headerBgColor="#F7F7F7"
          columns={columns}
          rows={rows}
          loading={loadingUsers}
          getRowId={(row) => row.user_email || row.id}
          pageSizeOptions={[5, 10, 20, 50, 100]}
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          onRowClick={(params) => {
            navigate(
              `${constRoute.masteradminUsers}?email=${encodeURIComponent(params.row.user_email)}`,
            );
          }}
        />
      </Box>

      {isModalOpen && <InviteModal />}

      <DeleteUserModal
        isOpen={openDeleteModal}
        closeModal={() => {
          setOpenDeleteModal(false);
          setUserEmailToDelete(null);
        }}
        onDeleteRecord={async () => {
          if (userEmailToDelete) {
            await handleUserDelete(userEmailToDelete);
            setOpenDeleteModal(false);
            setUserEmailToDelete(null);
          }
        }}
        username={
          allUsers["account holders"]?.find(
            (el) => el.user_email === userEmailToDelete,
          )?.account_holder || "Unknown User"
        }
      />

      {selectedUserForCredits && (
        <AddTokensModal
          isOpen={openAddCreditsModal}
          closeModal={async ({ fetchUserDetails }) => {
            setOpenAddCreditsModal(false);
            setSelectedUserForCredits(null);
            if (fetchUserDetails) {
              await receiveAllUsers();
            }
          }}
          user={selectedUserForCredits}
        />
      )}
    </Box>
  );
};

export default Users;
